'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useRegionPricing } from '@/features/payment/hooks/useRegionPricing';

interface PromoBannerProps {
    isVisible: boolean;
    onClose: () => void;
}

const PromoBanner = ({ isVisible, onClose }: PromoBannerProps) => {
    const [copied, setCopied] = useState(false);
    const { isIndia, currencySymbol, discountedPrice, originalPrice, currency } = useRegionPricing();

    const handleCopyCode = () => {
        navigator.clipboard.writeText('FLAT500');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isVisible) return null;

    const locale = currency === 'INR' ? 'en-IN' : 'en-US';
    const promoPrice = isIndia ? 999 : 20;
    const strikePrice = isIndia ? 1499 : 25;

    return (
        <div className="fixed top-0 left-0 right-0 z-[110] h-12 bg-violet-600 text-white flex items-center justify-between px-3 md:px-6">
            <div className="flex-1 flex items-center justify-center gap-1.5 sm:gap-3 text-xs sm:text-sm">
                {/* Main message */}
                <span className="hidden lg:inline text-violet-100">Ready to validate your idea?</span>
                <span className="font-medium">Launch your waitlist</span>

                {/* Pricing */}
                <span className="hidden sm:flex items-center gap-1.5">
                    <span className="text-violet-200">—</span>
                    <span className="line-through text-violet-300 text-xs">{currencySymbol}{strikePrice.toLocaleString(locale)}</span>
                    <span className="font-bold text-white">{currencySymbol}{promoPrice.toLocaleString(locale)}</span>
                    <span className="text-violet-200 text-xs hidden md:inline">(one-time, forever)</span>
                </span>

                {/* Code */}
                <span className="flex items-center gap-1.5">
                    <span className="text-violet-200 hidden sm:inline">use</span>
                    <button
                        onClick={handleCopyCode}
                        className="flex items-center gap-1 px-2 py-0.5 bg-white text-violet-700 rounded font-mono text-xs sm:text-sm font-bold"
                        title="Click to copy"
                    >
                        FLAT500
                        {copied ? (
                            <Icon name="CheckIcon" size={12} className="text-emerald-600" />
                        ) : (
                            <Icon name="DocumentDuplicateIcon" size={12} className="text-violet-400" />
                        )}
                    </button>
                </span>
            </div>

            <button
                onClick={onClose}
                className="p-1 hover:bg-violet-500 rounded transition-colors ml-2 text-violet-200 hover:text-white flex-shrink-0"
                aria-label="Dismiss banner"
            >
                <Icon name="XMarkIcon" size={18} />
            </button>
        </div>
    );
};

export default PromoBanner;
